import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useEffect, useState } from 'react';
import { cn } from 'components/app-components/ui/utils';
import CloseModalButton from './CloseModalButton';

const CLOSE_GRACE_MS = 300;

/**
 * Shared modal component backed by Radix Dialog.
 * Preserves the full legacy API surface (title, footer, fullScreen, isDarkMode, etc.).
 */
const GenericModal = ({
  open,
  onClose,
  titleIcon,
  title,
  titleCenter = false,
  subTitle,
  footer,
  children,
  fullScreen,
  showHeaderBorder = true,
  position,
  contentWrapperClassName,
  width,
  maxHeight,
  rightSideButton,
  leftSideButton,
  moveFocusToTop,
  isProcessing,
  dialogClass: _dialogClass = 'relative z-10',
  hasOnlyCloseButton = false,
  isDarkMode = false,
  preventOutsideClick = false,
  footerClassName,
  keepMountedOnClose = false,
}: {
  open: boolean;
  onClose: () => void;
  titleIcon?: React.ReactNode;
  title?: string | React.ReactNode;
  titleCenter?: boolean;
  subTitle?: string;
  footer?: React.ReactNode;
  children: React.ReactNode;
  fullScreen?: boolean;
  showHeaderBorder?: boolean;
  position?: string;
  contentWrapperClassName?: string;
  /**
   * @description weight of the modal
   * @example weight="32rem" or weight="50%"
   */
  width?: string;
  /**
   * @description max height of the modal
   * @example maxHeight="80vh"
   */
  maxHeight?: string;
  rightSideButton?: React.ReactNode;
  leftSideButton?: React.ReactNode;
  moveFocusToTop?: boolean;
  isProcessing?: boolean;
  dialogClass?: string;
  hasOnlyCloseButton?: boolean;
  isDarkMode?: boolean;
  preventOutsideClick?: boolean;
  footerClassName?: string;
  /** Delay unmount by CLOSE_GRACE_MS so blob URLs (e.g. PDFDownloadLink) remain valid through the close animation. */
  keepMountedOnClose?: boolean;
}) => {
  const [shouldRender, setShouldRender] = useState(open);

  useEffect(() => {
    if (!keepMountedOnClose) return;
    if (open) {
      setShouldRender(true);
      return;
    }
    const timer = window.setTimeout(() => setShouldRender(false), CLOSE_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [open, keepMountedOnClose]);

  if (keepMountedOnClose ? !shouldRender : !open) return null;

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) return;
        if (preventOutsideClick || isProcessing) return;
        onClose();
      }}
    >
      <DialogPrimitive.Portal forceMount>
        {/* Overlay */}
        <DialogPrimitive.Overlay
          forceMount
          className="fixed inset-0 z-50 bg-black/25 transition-opacity duration-200 data-[state=closed]:pointer-events-none data-[state=closed]:opacity-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />

        {/* Fixed wrapper owns z-index; dialogClass goes on an inner div so "relative z-10" cannot override "fixed". */}
        <div className={`fixed inset-0 z-50 overflow-y-auto ${!open ? 'pointer-events-none' : ''}`}>
          <div
            className={`flex ${position ? position : 'min-h-full items-center'} justify-center p-4 text-center md:p-6`}
          >
            <DialogPrimitive.Content
              forceMount
              aria-label={typeof title === 'string' ? title : 'Modal dialog'}
              className={cn(
                // Bounding the dialog to the viewport keeps the body the only scroller. Without it the
                // body's own cap stacks on top of the header and spills into the fixed wrapper above,
                // giving a second scrollbar outside the dialog on short viewports.
                'flex max-h-[calc(100dvh-4rem)] flex-col',
                'max-w-[calc(100vw-2rem)] transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl outline-none transition-all duration-200 data-[state=closed]:pointer-events-none data-[state=closed]:opacity-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                fullScreen ? 'w-full' : width,
              )}
              onEscapeKeyDown={(event) => {
                if (preventOutsideClick || isProcessing) event.preventDefault();
              }}
              onPointerDownOutside={(event) => {
                if (preventOutsideClick || isProcessing) {
                  event.preventDefault();
                  return;
                }
                const target = event.detail.originalEvent.target as HTMLElement;
                if (target?.closest?.('[data-radix-popper-content-wrapper], .react-datepicker-popper')) {
                  event.preventDefault();
                  return;
                }
              }}
              onFocusOutside={(event) => {
                if (preventOutsideClick || isProcessing) {
                  event.preventDefault();
                  return;
                }
                const target = event.target as HTMLElement;
                if (target?.closest?.('[data-radix-popper-content-wrapper], .react-datepicker-popper')) {
                  event.preventDefault();
                  return;
                }
              }}
            >
              {!title && !hasOnlyCloseButton && (
                <DialogPrimitive.Title className="sr-only">Dialog</DialogPrimitive.Title>
              )}
              {(title || hasOnlyCloseButton) && (
                <div
                  className={`${
                    showHeaderBorder ? 'border-b border-gray-200 pb-4' : 'border-transparent'
                  } sticky top-0 flex shrink-0 flex-row items-start rounded-t-lg ${
                    isDarkMode ? 'bg-neutral-900' : 'bg-white'
                  } px-4 pt-4 md:px-6`}
                  {...(moveFocusToTop && { tabIndex: 0 })}
                >
                  <div className="flex w-full justify-between">
                    {leftSideButton && <div className="ml-auto flex items-center justify-center">{leftSideButton}</div>}
                    <div className="flex w-full flex-col">
                      <div className={`mt-1 flex items-center gap-2 ${titleCenter ? 'flex-1 justify-center' : ''}`}>
                        {titleIcon && titleIcon}
                        <DialogPrimitive.Title
                          className={`${isDarkMode ? 'text-white' : 'text-neutral-900'} text-lg font-medium leading-6`}
                        >
                          {title}
                        </DialogPrimitive.Title>
                      </div>
                      {subTitle && (
                        <DialogPrimitive.Description
                          className={`mt-2 flex max-w-lg items-center text-sm text-neutral-500 ${titleCenter ? 'flex-1 justify-center' : ''}`}
                        >
                          {subTitle}
                        </DialogPrimitive.Description>
                      )}
                    </div>

                    <div className={`${titleCenter ? 'absolute right-2.5 top-2.5' : 'ml-auto'}`}>
                      {rightSideButton ? (
                        rightSideButton
                      ) : (
                        <CloseModalButton
                          onClose={() => {
                            !isProcessing && onClose();
                          }}
                          className="ml-auto"
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div
                style={maxHeight ? { maxHeight } : undefined}
                className={`min-h-0 flex-1 overflow-y-auto p-4 md:p-6 ${maxHeight ? '' : 'max-h-[80vh]'} ${contentWrapperClassName || ''} ${
                  isDarkMode ? 'bg-neutral-900' : 'bg-white'
                }`}
              >
                {children}
              </div>
              {footer && (
                <div className={`shrink-0 border-t border-gray-200 p-4 md:p-6 ${footerClassName || ''}`}>{footer}</div>
              )}
            </DialogPrimitive.Content>
          </div>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default GenericModal;
